<script setup>
// NOTICE: KAPTION-INTEGRATION
// team-filter-scope.SETTING.4 — what the group filter in the conversation list
// means on this account. Gated by KAPTION_TEAM_FILTER_SCOPE so the section only
// appears where it has been enabled; the setting itself is honored regardless.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import SectionLayout from './SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const selectedMode = ref('team');
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const isFlagEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_TEAM_FILTER_SCOPE')
);

const options = [
  {
    value: 'team',
    labelKey: 'GENERAL_SETTINGS.FORM.TEAM_FILTER_SCOPE.OPTION_TEAM',
    descKey: 'GENERAL_SETTINGS.FORM.TEAM_FILTER_SCOPE.OPTION_TEAM_DESC',
  },
  {
    value: 'team_and_members',
    labelKey: 'GENERAL_SETTINGS.FORM.TEAM_FILTER_SCOPE.OPTION_TEAM_AND_MEMBERS',
    descKey:
      'GENERAL_SETTINGS.FORM.TEAM_FILTER_SCOPE.OPTION_TEAM_AND_MEMBERS_DESC',
  },
];

watch(
  currentAccount,
  () => {
    const mode = currentAccount.value?.settings?.team_filter_scope || 'team';
    selectedMode.value = mode;
  },
  { deep: true, immediate: true }
);

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount(
      { team_filter_scope: selectedMode.value },
      { silent: true }
    );
    useAlert(t('GENERAL_SETTINGS.FORM.TEAM_FILTER_SCOPE.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.TEAM_FILTER_SCOPE.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    v-if="isFlagEnabled"
    :title="t('GENERAL_SETTINGS.FORM.TEAM_FILTER_SCOPE.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.TEAM_FILTER_SCOPE.DESCRIPTION')"
    with-border
  >
    <form class="grid gap-4" @submit.prevent="handleSubmit">
      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
      >
        <label
          v-for="option in options"
          :key="option.value"
          class="p-3 flex items-start gap-3 cursor-pointer hover:bg-n-alpha-1 transition-colors"
          :class="{ 'bg-n-alpha-1': selectedMode === option.value }"
        >
          <input
            v-model="selectedMode"
            type="radio"
            name="team_filter_scope"
            :value="option.value"
            class="mt-0.5 accent-woot-500"
          />
          <div>
            <span class="font-medium">{{ t(option.labelKey) }}</span>
            <p class="text-n-slate-11 text-xs mt-0.5">
              {{ t(option.descKey) }}
            </p>
          </div>
        </label>
      </div>
      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :label="t('GENERAL_SETTINGS.FORM.TEAM_FILTER_SCOPE.UPDATE_BUTTON')"
        />
      </div>
    </form>
  </SectionLayout>
</template>
